import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, interval, map, Subject, takeUntil } from 'rxjs';
import { cookieStorage, signalStorage } from 'wflow-cdk';
import { WfIdleStrategy } from 'wflow-features/auth';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import { InteractionListenerService } from './interaction-listener.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const IDLE_CHECK_INTERVAL = 60 * 1000; // 1 minute

@Injectable({
  providedIn: 'root',
})
export class IdleStrategy implements WfIdleStrategy {
  readonly interactionListenerSvc = inject(InteractionListenerService);
  readonly destroyRef = inject(DestroyRef);
  private readonly productTrackingSvc = inject(ProductEventTrackingService);

  readonly isIdle$ = new BehaviorSubject<boolean>(false);

  /** Guards the one-shot idle-logout event until the session is reset. */
  private idleLogoutTracked = false;

  /**
   * The minimal inactivity timeout in minutes.
   */
  private idleTimeoutMinutes = signalStorage<number | null>(
    'w._idle_timeout_minutes_',
    {
      storage: cookieStorage,
      serializer: (value) => value?.toString() ?? null,
      deserializer: (value) => (value ? parseInt(value) : null),
      initialValue: null,
    },
  );

  private onTimeoutChangedCallback: (timeoutSeconds: number | null) => void =
    () => void 0;
  /**
   * To register a callback to be called when the timeout changes.
   * @param callback - The callback to call when the timeout changes.
   */
  registerOnTimeoutChangedCallback(
    callback: (isIdle: boolean, timeoutSeconds: number | null) => void,
  ) {
    this.onTimeoutChangedCallback = (activityTimestamp) => {
      // If the activity timestamp is not set, return.
      if (!activityTimestamp) return;

      // If the timeout minutes is not set, return.
      const timeoutMinutes = this.idleTimeoutMinutes();
      if (!timeoutMinutes) return;

      const isIdle = this.isIdle();

      // Area I [Frustration] — the inactivity policy is about to end the session
      // (value = the configured timeout in minutes that triggered it). Hooked
      // here rather than on the `#inactive` logout page, which no longer knows
      // the timeout. Emitted once: the check-in interval keeps running until the
      // redirect completes.
      if (isIdle && !this.idleLogoutTracked) {
        this.idleLogoutTracked = true;
        this.productTrackingSvc.track('onb_idle_logout', timeoutMinutes);
      }

      const timeoutSeconds = timeoutMinutes * 60;
      callback(isIdle, isIdle ? null : timeoutSeconds);
    };

    this.interactionListenerSvc.registerOnActivityTimestampChangedCallback(
      this.onTimeoutChangedCallback.bind(this),
    );
  }

  /**
   * Start the idle strategy with the given timeout in minutes.
   * @param idleTimeoutMinutes - The timeout in minutes.
   */
  startWith(idleTimeoutMinutes: number | null) {
    if (!!idleTimeoutMinutes && !this.interactionListenerSvc.isActivated) {
      this.idleTimeoutMinutes.set(idleTimeoutMinutes);
      this.interactionListenerSvc.listen(idleTimeoutMinutes * 60);
      // Refresh activity timestamp to prevent stale values from triggering immediate logout
      this.interactionListenerSvc.refreshActivityTimestamp();
      this.startCheckInInterval();
    } else {
      this.reset();
    }
  }

  reset() {
    this.idleLogoutTracked = false;
    this.idleTimeoutMinutes.clear();
    this.interactionListenerSvc.stop();
    this.stopCheckInInterval();
  }

  /**
   * Check if the user is idle.
   * @returns True if the user is idle, false otherwise.
   */
  isIdle() {
    const timeoutMinutes = this.idleTimeoutMinutes();
    if (!timeoutMinutes) return false;
    return this.interactionListenerSvc.isIdle(timeoutMinutes * 60);
  }

  /**
   * Refresh the activity timestamp to the current time.
   * Used when receiving login from another tab to prevent stale timestamps.
   */
  refreshActivityTimestamp() {
    this.interactionListenerSvc.refreshActivityTimestamp();
  }

  private readonly stopIntervalCheck$ = new Subject<void>();

  private startCheckInInterval() {
    interval(IDLE_CHECK_INTERVAL)
      .pipe(
        takeUntil(this.stopIntervalCheck$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.onTimeoutChangedCallback(
          this.interactionListenerSvc.lastActivityTimestamp(),
        );
      });
  }

  private stopCheckInInterval() {
    this.stopIntervalCheck$.next();
  }
}
